/*
 * Excel tool layer.
 * Each tool has (1) an OpenAI-style JSON schema the model sees, and
 * (2) a `run` function that executes it against the live workbook via Office.js.
 *
 * Writing tools set `mutating: true` so the UI can require confirmation.
 *
 * Some tools rely on Excel JS API features newer than the manifest's
 * DefaultMinVersion. They declare `minApi` (the ExcelApi requirement set they
 * need); runTool checks support before running so older Excel builds (e.g. some
 * perpetual Office 2021 installs that haven't been updated, or Office 2019) get
 * a friendly message instead of a runtime crash. See:
 * https://learn.microsoft.com/office/dev/add-ins/reference/requirement-sets/excel-api-requirement-sets
 */

/* global Excel, Office */

function ok(data) {
  return { ok: true, ...data };
}
function err(message) {
  return { ok: false, error: message };
}

/**
 * Check whether the host Excel supports a given ExcelApi requirement set.
 * Returns true when the host can't be queried (e.g. unit tests), so behaviour
 * is unchanged on fully-featured hosts.
 */
function isApiSupported(minApi) {
  if (!minApi) return true;
  try {
    if (typeof Office !== "undefined" && Office.context && Office.context.requirements) {
      return Office.context.requirements.isSetSupported("ExcelApi", minApi);
    }
  } catch (e) {
    // fall through
  }
  return true;
}

// Convert a 2D array to a compact CSV-ish string for the model.
function toCsv(values) {
  return values
    .map((row) =>
      row
        .map((c) => {
          if (c === null || c === undefined) return "";
          const s = String(c);
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(",")
    )
    .join("\n");
}

export const tools = {
  list_sheets: {
    schema: {
      type: "function",
      function: {
        name: "list_sheets",
        description:
          "List all worksheets in the open workbook with their used-range dimensions. Call this first to understand the workbook structure.",
        parameters: { type: "object", properties: {}, required: [] },
      },
    },
    mutating: false,
    async run() {
      return Excel.run(async (ctx) => {
        const sheets = ctx.workbook.worksheets;
        sheets.load("items/name,items/position,items/visibility");
        await ctx.sync();
        const result = [];
        for (const s of sheets.items) {
          const used = s.getUsedRangeOrNullObject(true);
          used.load("address,rowCount,columnCount");
          result.push({ sheet: s, used });
        }
        await ctx.sync();
        return ok({
          sheets: result.map(({ sheet, used }) => ({
            name: sheet.name,
            position: sheet.position,
            visibility: sheet.visibility,
            usedRange: used.isNullObject ? null : used.address,
            rows: used.isNullObject ? 0 : used.rowCount,
            columns: used.isNullObject ? 0 : used.columnCount,
          })),
        });
      });
    },
  },

  read_range: {
    schema: {
      type: "function",
      function: {
        name: "read_range",
        description:
          "Read cell values from a range as CSV. Provide a sheet name and an A1 range (e.g. 'A1:F40'). If range is omitted, the used range of the sheet is read. Use this to inspect data before answering or editing.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
            range: {
              type: "string",
              description: "A1 notation range, e.g. 'A1:D20'. Omit to read the whole used range.",
            },
            formulas: {
              type: "boolean",
              description: "If true, return formulas instead of computed values.",
            },
          },
          required: ["sheet"],
        },
      },
    },
    mutating: false,
    async run({ sheet, range, formulas }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const target = range ? ws.getRange(range) : ws.getUsedRangeOrNullObject(true);
        target.load("address,rowCount,columnCount,values,formulas");
        await ctx.sync();
        if (target.isNullObject) return err(`Range is empty on "${sheet}".`);
        const data = formulas ? target.formulas : target.values;
        return ok({
          address: target.address,
          rows: target.rowCount,
          columns: target.columnCount,
          csv: toCsv(data),
        });
      });
    },
  },

  write_range: {
    schema: {
      type: "function",
      function: {
        name: "write_range",
        description:
          "Write a rectangular block of values or formulas into a range. `values` is a 2D array (rows of cells) whose shape must match the range. Use a leading '=' for formulas. This MODIFIES the workbook.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
            range: { type: "string", description: "Top-left anchored A1 range, e.g. 'B2:C5'." },
            values: {
              type: "array",
              description: "2D array of cell contents. Strings starting with '=' are treated as formulas.",
              items: { type: "array", items: {} },
            },
          },
          required: ["sheet", "range", "values"],
        },
      },
    },
    mutating: true,
    async run({ sheet, range, values }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const target = ws.getRange(range);
        target.load("rowCount,columnCount,address");
        await ctx.sync();
        if (target.rowCount !== values.length || target.columnCount !== (values[0] || []).length) {
          return err(
            `Shape mismatch: range ${target.address} is ${target.rowCount}x${target.columnCount} but values is ${
              values.length
            }x${(values[0] || []).length}.`
          );
        }
        target.values = values;
        await ctx.sync();
        return ok({ address: target.address, written: `${target.rowCount}x${target.columnCount}` });
      });
    },
  },

  add_sheet: {
    schema: {
      type: "function",
      function: {
        name: "add_sheet",
        description: "Create a new worksheet. This MODIFIES the workbook.",
        parameters: {
          type: "object",
          properties: { name: { type: "string", description: "Name for the new sheet." } },
          required: ["name"],
        },
      },
    },
    mutating: true,
    async run({ name }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.add(name);
        ws.activate();
        await ctx.sync();
        return ok({ created: name });
      });
    },
  },

  apply_format: {
    schema: {
      type: "function",
      function: {
        name: "apply_format",
        description:
          "Apply formatting to a range: bold, italic, font color, fill color, number format, and column autofit. This MODIFIES the workbook.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string" },
            range: { type: "string", description: "A1 range to format." },
            bold: { type: "boolean" },
            italic: { type: "boolean" },
            fontColor: { type: "string", description: "Hex color e.g. '#FF0000'." },
            fillColor: { type: "string", description: "Hex background color e.g. '#FFF2CC'." },
            numberFormat: { type: "string", description: "Excel number format e.g. '#,##0.00' or '0.0%'." },
            autofit: { type: "boolean", description: "Autofit column widths." },
          },
          required: ["sheet", "range"],
        },
      },
    },
    mutating: true,
    async run({ sheet, range, bold, italic, fontColor, fillColor, numberFormat, autofit }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const r = ws.getRange(range);
        if (bold !== undefined) r.format.font.bold = bold;
        if (italic !== undefined) r.format.font.italic = italic;
        if (fontColor) r.format.font.color = fontColor;
        if (fillColor) r.format.fill.color = fillColor;
        if (numberFormat) {
          r.load("rowCount,columnCount");
          await ctx.sync();
          r.numberFormat = Array.from({ length: r.rowCount }, () =>
            Array.from({ length: r.columnCount }, () => numberFormat)
          );
        }
        if (autofit) r.format.autofitColumns();
        await ctx.sync();
        return ok({ address: range, formatted: true });
      });
    },
  },

  clear_range: {
    schema: {
      type: "function",
      function: {
        name: "clear_range",
        description: "Clear contents and/or formats of a range. This MODIFIES the workbook.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string" },
            range: { type: "string" },
            what: {
              type: "string",
              enum: ["contents", "formats", "all"],
              description: "What to clear. Defaults to 'contents'.",
            },
          },
          required: ["sheet", "range"],
        },
      },
    },
    mutating: true,
    async run({ sheet, range, what }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const map = { contents: Excel.ClearApplyTo.contents, formats: Excel.ClearApplyTo.formats, all: Excel.ClearApplyTo.all };
        ws.getRange(range).clear(map[what] || Excel.ClearApplyTo.contents);
        await ctx.sync();
        return ok({ cleared: range });
      });
    },
  },

  get_selection: {
    schema: {
      type: "function",
      function: {
        name: "get_selection",
        description: "Get the address and values of the cells the user currently has selected.",
        parameters: { type: "object", properties: {}, required: [] },
      },
    },
    mutating: false,
    async run() {
      return Excel.run(async (ctx) => {
        const sel = ctx.workbook.getSelectedRange();
        sel.load("address,values,rowCount,columnCount,worksheet/name");
        await ctx.sync();
        return ok({ sheet: sel.worksheet.name, address: sel.address, csv: toCsv(sel.values) });
      });
    },
  },

  // ============================================================================
  // EXCEL TABLES (ListObjects)
  // ============================================================================

  create_table: {
    schema: {
      type: "function",
      function: {
        name: "create_table",
        description: "Create a table from a range with optional headers and initial data. This MODIFIES the workbook.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
            address: { type: "string", description: "A1 range for the table, e.g. 'A1:D10'." },
            name: { type: "string", description: "Optional table name." },
            hasHeaders: { type: "boolean", description: "Whether first row contains headers." },
            headers: {
              type: "array",
              description: "Optional column headers array.",
              items: { type: "string" },
            },
            rows: {
              type: "array",
              description: "Optional 2D array of initial data rows.",
              items: { type: "array", items: {} },
            },
          },
          required: ["sheet", "address"],
        },
      },
    },
    mutating: true,
    async run({ sheet, address, name, hasHeaders, headers, rows }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const table = ws.tables.add(address, hasHeaders || !!headers);
        table.load("name,id");
        if (name) table.name = name;
        if (headers) {
          const headerRange = table.getHeaderRowRange();
          headerRange.values = [headers];
        }
        if (rows && rows.length > 0) {
          table.rows.add(null, rows);
        }
        await ctx.sync();
        return ok({ name: table.name, id: table.id, created: true });
      });
    },
  },

  list_tables: {
    schema: {
      type: "function",
      function: {
        name: "list_tables",
        description: "List all tables in a sheet or entire workbook with their names, ranges, and columns.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Optional worksheet name. Omit to list all sheets." },
          },
          required: [],
        },
      },
    },
    mutating: false,
    async run({ sheet }) {
      return Excel.run(async (ctx) => {
        const tables = [];
        if (sheet) {
          const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
          await ctx.sync();
          if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
          const tbls = ws.tables;
          tbls.load("items/name,items/id,items/style");
          await ctx.sync();
          for (const t of tbls.items) {
            const range = t.getRange();
            range.load("address");
            const cols = t.columns;
            cols.load("items/name");
            await ctx.sync();
            tables.push({
              sheet,
              name: t.name,
              id: t.id,
              style: t.style,
              address: range.address,
              columns: cols.items.map((c) => c.name),
            });
          }
        } else {
          const sheets = ctx.workbook.worksheets;
          sheets.load("items/name");
          await ctx.sync();
          for (const s of sheets.items) {
            const tbls = s.tables;
            tbls.load("items/name,items/id,items/style");
            await ctx.sync();
            for (const t of tbls.items) {
              const range = t.getRange();
              range.load("address");
              const cols = t.columns;
              cols.load("items/name");
              await ctx.sync();
              tables.push({
                sheet: s.name,
                name: t.name,
                id: t.id,
                style: t.style,
                address: range.address,
                columns: cols.items.map((c) => c.name),
              });
            }
          }
        }
        return ok({ tables, count: tables.length });
      });
    },
  },

  read_table_data: {
    schema: {
      type: "function",
      function: {
        name: "read_table_data",
        description: "Read data from a named table — headers, body, specific column, or specific row.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
            table: { type: "string", description: "Table name." },
            column: { type: "string", description: "Optional column name to read." },
            row: { type: "number", description: "Optional row index (0-based) to read." },
          },
          required: ["sheet", "table"],
        },
      },
    },
    mutating: false,
    async run({ sheet, table, column, row }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const tbl = ws.tables.getItemOrNullObject(table);
        await ctx.sync();
        if (tbl.isNullObject) return err(`Table "${table}" not found.`);
        if (column) {
          const col = tbl.columns.getItem(column);
          const dataRange = col.getDataBodyRange();
          dataRange.load("values");
          await ctx.sync();
          return ok({ table, column, csv: toCsv(dataRange.values) });
        } else if (row !== undefined) {
          const rowObj = tbl.rows.getItemAt(row);
          rowObj.load("values");
          await ctx.sync();
          return ok({ table, row, csv: toCsv([rowObj.values]) });
        } else {
          const headerRange = tbl.getHeaderRowRange();
          const bodyRange = tbl.getDataBodyRange();
          headerRange.load("values");
          bodyRange.load("values");
          await ctx.sync();
          return ok({
            table,
            headers: toCsv(headerRange.values),
            data: toCsv(bodyRange.values),
            rowCount: bodyRange.values.length,
            columnCount: bodyRange.values[0] ? bodyRange.values[0].length : 0,
          });
        }
      });
    },
  },

  add_table_rows: {
    schema: {
      type: "function",
      function: {
        name: "add_table_rows",
        description: "Add new rows to an existing table. This MODIFIES the workbook.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
            table: { type: "string", description: "Table name." },
            values: {
              type: "array",
              description: "2D array of row data to add.",
              items: { type: "array", items: {} },
            },
          },
          required: ["sheet", "table", "values"],
        },
      },
    },
    mutating: true,
    async run({ sheet, table, values }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const tbl = ws.tables.getItemOrNullObject(table);
        await ctx.sync();
        if (tbl.isNullObject) return err(`Table "${table}" not found.`);
        tbl.rows.add(null, values);
        await ctx.sync();
        return ok({ table, rowsAdded: values.length });
      });
    },
  },

  // ============================================================================
  // NAMED RANGES
  // ============================================================================

  create_named_range: {
    schema: {
      type: "function",
      function: {
        name: "create_named_range",
        description: "Create a named range (workbook-scoped or sheet-scoped). This MODIFIES the workbook.",
        parameters: {
          type: "object",
          properties: {
            name: { type: "string", description: "Name for the range." },
            sheet: { type: "string", description: "Worksheet name." },
            range: { type: "string", description: "A1 notation range, e.g. 'A1:D20'." },
            scope: {
              type: "string",
              enum: ["workbook", "sheet"],
              description: "Scope of the named range. Defaults to 'workbook'.",
            },
          },
          required: ["name", "sheet", "range"],
        },
      },
    },
    mutating: true,
    async run({ name, sheet, range, scope }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const targetRange = ws.getRange(range);
        if (scope === "sheet") {
          ws.names.add(name, targetRange);
        } else {
          ctx.workbook.names.add(name, targetRange);
        }
        await ctx.sync();
        return ok({ name, sheet, range, scope: scope || "workbook", created: true });
      });
    },
  },

  list_named_ranges: {
    schema: {
      type: "function",
      function: {
        name: "list_named_ranges",
        description: "List all named ranges in the workbook.",
        parameters: { type: "object", properties: {}, required: [] },
      },
    },
    mutating: false,
    async run() {
      return Excel.run(async (ctx) => {
        const names = ctx.workbook.names;
        names.load("items/name,items/type,items/visibility");
        await ctx.sync();
        const result = [];
        for (const n of names.items) {
          try {
            const range = n.getRangeOrNullObject();
            range.load("address");
            await ctx.sync();
            result.push({
              name: n.name,
              type: n.type,
              visibility: n.visibility,
              address: range.isNullObject ? null : range.address,
            });
          } catch (e) {
            result.push({ name: n.name, type: n.type, visibility: n.visibility, address: null });
          }
        }
        return ok({ namedRanges: result, count: result.length });
      });
    },
  },

  read_named_range: {
    schema: {
      type: "function",
      function: {
        name: "read_named_range",
        description: "Read values from a named range by name.",
        parameters: {
          type: "object",
          properties: {
            name: { type: "string", description: "Named range name." },
          },
          required: ["name"],
        },
      },
    },
    mutating: false,
    async run({ name }) {
      return Excel.run(async (ctx) => {
        const namedItem = ctx.workbook.names.getItemOrNullObject(name);
        await ctx.sync();
        if (namedItem.isNullObject) return err(`Named range "${name}" not found.`);
        const range = namedItem.getRange();
        range.load("values,address,rowCount,columnCount");
        await ctx.sync();
        return ok({
          name,
          address: range.address,
          rows: range.rowCount,
          columns: range.columnCount,
          csv: toCsv(range.values),
        });
      });
    },
  },

  delete_named_range: {
    schema: {
      type: "function",
      function: {
        name: "delete_named_range",
        description: "Delete a named range. This MODIFIES the workbook.",
        parameters: {
          type: "object",
          properties: {
            name: { type: "string", description: "Named range name." },
          },
          required: ["name"],
        },
      },
    },
    mutating: true,
    async run({ name }) {
      return Excel.run(async (ctx) => {
        const namedItem = ctx.workbook.names.getItemOrNullObject(name);
        await ctx.sync();
        if (namedItem.isNullObject) return err(`Named range "${name}" not found.`);
        namedItem.delete();
        await ctx.sync();
        return ok({ name, deleted: true });
      });
    },
  },

  // ============================================================================
  // CHARTS
  // ============================================================================

  create_chart: {
    schema: {
      type: "function",
      function: {
        name: "create_chart",
        description:
          "Create a chart from a data range. This MODIFIES the workbook. Common chartType values: Line, ColumnClustered, ColumnStacked, BarClustered, BarStacked, Pie, PieExploded, Doughnut, Area, AreaStacked, XYScatter, Radar. Also supports newer types: Waterfall, Histogram, Pareto, Treemap, Sunburst, Boxwhisker, Funnel, RegionMap.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
            range: { type: "string", description: "A1 data source range." },
            chartType: { type: "string", description: "Chart type enum value." },
            title: { type: "string", description: "Optional chart title." },
            position: {
              type: "object",
              description: "Optional position {topLeft, bottomRight}.",
              properties: {
                topLeft: { type: "string" },
                bottomRight: { type: "string" },
              },
            },
            seriesBy: {
              type: "string",
              enum: ["auto", "rows", "columns"],
              description: "How to interpret data series. Defaults to 'auto'.",
            },
          },
          required: ["sheet", "range", "chartType"],
        },
      },
    },
    mutating: true,
    minApi: "1.7",
    async run({ sheet, range, chartType, title, position, seriesBy }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const dataRange = ws.getRange(range);
        const seriesByEnum =
          seriesBy === "rows"
            ? Excel.ChartSeriesBy.rows
            : seriesBy === "columns"
              ? Excel.ChartSeriesBy.columns
              : Excel.ChartSeriesBy.auto;
        const chart = ws.charts.add(chartType, dataRange, seriesByEnum);
        chart.load("name");
        if (title) {
          chart.title.text = title;
        }
        if (position) {
          if (position.topLeft && position.bottomRight) {
            chart.setPosition(position.topLeft, position.bottomRight);
          }
        }
        await ctx.sync();
        return ok({ name: chart.name, chartType, created: true });
      });
    },
  },

  list_charts: {
    schema: {
      type: "function",
      function: {
        name: "list_charts",
        description: "List all charts on a sheet.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
          },
          required: ["sheet"],
        },
      },
    },
    mutating: false,
    minApi: "1.7",
    async run({ sheet }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const charts = ws.charts;
        charts.load("items/name,items/chartType,items/left,items/top,items/width,items/height");
        await ctx.sync();
        const result = charts.items.map((c) => ({
          name: c.name,
          chartType: c.chartType,
          left: c.left,
          top: c.top,
          width: c.width,
          height: c.height,
        }));
        return ok({ charts: result, count: result.length });
      });
    },
  },

  format_chart: {
    schema: {
      type: "function",
      function: {
        name: "format_chart",
        description: "Format an existing chart — title, legend, axes, colors, chart type. This MODIFIES the workbook.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
            chart: { type: "string", description: "Chart name or index." },
            title: { type: "string", description: "Chart title text." },
            chartType: { type: "string", description: "Change chart type." },
            legendPosition: {
              type: "string",
              enum: ["top", "bottom", "left", "right", "corner", "none"],
              description: "Legend position.",
            },
            categoryAxisTitle: { type: "string", description: "Category axis title." },
            valueAxisTitle: { type: "string", description: "Value axis title." },
            dataLabels: { type: "boolean", description: "Show data labels." },
          },
          required: ["sheet", "chart"],
        },
      },
    },
    mutating: true,
    minApi: "1.7",
    async run({ sheet, chart, title, chartType, legendPosition, categoryAxisTitle, valueAxisTitle, dataLabels }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const charts = ws.charts;
        charts.load("items/name");
        await ctx.sync();
        let targetChart = null;
        const idx = parseInt(chart);
        if (!isNaN(idx) && idx >= 0 && idx < charts.items.length) {
          targetChart = charts.items[idx];
        } else {
          targetChart = charts.getItemOrNullObject(chart);
        }
        if (!targetChart) return err(`Chart "${chart}" not found.`);
        if (title) {
          targetChart.title.text = title;
          targetChart.title.visible = true;
        }
        if (chartType) {
          targetChart.chartType = chartType;
        }
        if (legendPosition) {
          const posMap = {
            top: Excel.ChartLegendPosition.top,
            bottom: Excel.ChartLegendPosition.bottom,
            left: Excel.ChartLegendPosition.left,
            right: Excel.ChartLegendPosition.right,
            corner: Excel.ChartLegendPosition.corner,
            none: Excel.ChartLegendPosition.none,
          };
          targetChart.legend.position = posMap[legendPosition] || Excel.ChartLegendPosition.right;
          targetChart.legend.visible = legendPosition !== "none";
        }
        if (categoryAxisTitle) {
          targetChart.axes.categoryAxis.title.text = categoryAxisTitle;
          targetChart.axes.categoryAxis.title.visible = true;
        }
        if (valueAxisTitle) {
          targetChart.axes.valueAxis.title.text = valueAxisTitle;
          targetChart.axes.valueAxis.title.visible = true;
        }
        if (dataLabels !== undefined) {
          targetChart.dataLabels.visible = dataLabels;
        }
        await ctx.sync();
        return ok({ chart, formatted: true });
      });
    },
  },

  // ============================================================================
  // CONDITIONAL FORMATTING
  // ============================================================================

  apply_conditional_format: {
    schema: {
      type: "function",
      function: {
        name: "apply_conditional_format",
        description:
          "Apply a conditional formatting rule to a range. This MODIFIES the workbook. Rule types: cellValue, colorScale, dataBar, iconSet, custom, textComparison, topBottom, presetCriteria.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
            range: { type: "string", description: "A1 range to format." },
            ruleType: {
              type: "string",
              enum: [
                "cellValue",
                "colorScale",
                "dataBar",
                "iconSet",
                "custom",
                "textComparison",
                "topBottom",
                "presetCriteria",
              ],
              description: "Type of conditional formatting rule.",
            },
            rule: { type: "object", description: "Rule object (varies by ruleType)." },
            format: {
              type: "object",
              description: "Format object with font/fill colors.",
              properties: {
                fontColor: { type: "string" },
                fillColor: { type: "string" },
                bold: { type: "boolean" },
                italic: { type: "boolean" },
              },
            },
            priority: { type: "number", description: "Rule priority (lower = higher priority)." },
          },
          required: ["sheet", "range", "ruleType", "rule"],
        },
      },
    },
    mutating: true,
    minApi: "1.6",
    async run({ sheet, range, ruleType, rule, format, priority }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const targetRange = ws.getRange(range);
        const cf = targetRange.conditionalFormats.add(ruleType);
        if (priority !== undefined) {
          cf.priority = priority;
        }
        if (ruleType === "cellValue" && rule.operator && rule.formula1) {
          cf.cellValue.format.font.color = format?.fontColor || "#000000";
          cf.cellValue.format.fill.color = format?.fillColor || "#FFFF00";
          if (format?.bold !== undefined) cf.cellValue.format.font.bold = format.bold;
          if (format?.italic !== undefined) cf.cellValue.format.font.italic = format.italic;
        } else if (ruleType === "custom" && rule.formula) {
          cf.custom.format.font.color = format?.fontColor || "#000000";
          cf.custom.format.fill.color = format?.fillColor || "#FFFF00";
        }
        await ctx.sync();
        return ok({ range, ruleType, applied: true });
      });
    },
  },

  clear_conditional_format: {
    schema: {
      type: "function",
      function: {
        name: "clear_conditional_format",
        description: "Remove all conditional formatting from a range. This MODIFIES the workbook.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
            range: { type: "string", description: "A1 range." },
          },
          required: ["sheet", "range"],
        },
      },
    },
    mutating: true,
    minApi: "1.6",
    async run({ sheet, range }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const targetRange = ws.getRange(range);
        targetRange.conditionalFormats.clearAll();
        await ctx.sync();
        return ok({ range, cleared: true });
      });
    },
  },

  // ============================================================================
  // AUTOFILTER / SORTING
  // ============================================================================

  apply_filter: {
    schema: {
      type: "function",
      function: {
        name: "apply_filter",
        description:
          "Sort data or apply filters to a range or table. This MODIFIES the workbook. For sorting: provide sortKeys array of {key: columnIndex, ascending: boolean}. For filtering: provide filterColumn and filterValues. Requires ExcelApi 1.9.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
            range: { type: "string", description: "A1 range to filter/sort." },
            sortKeys: {
              type: "array",
              description: "Array of sort keys {key: columnIndex, ascending: boolean}.",
              items: {
                type: "object",
                properties: {
                  key: { type: "number" },
                  ascending: { type: "boolean" },
                },
              },
            },
            filterColumn: { type: "string", description: "Column name or index to filter." },
            filterValues: {
              type: "array",
              description: "Array of values to show.",
              items: { type: "string" },
            },
            clearFilters: { type: "boolean", description: "Clear all filters if true." },
          },
          required: ["sheet", "range"],
        },
      },
    },
    mutating: true,
    minApi: "1.9",
    async run({ sheet, range, sortKeys, filterColumn, filterValues, clearFilters }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const targetRange = ws.getRange(range);
        if (clearFilters) {
          ws.autoFilter.clearAll();
          await ctx.sync();
          return ok({ range, cleared: true });
        }
        if (sortKeys && sortKeys.length > 0) {
          const sortFields = sortKeys.map((sk) => ({
            key: sk.key,
            ascending: sk.ascending !== false,
          }));
          targetRange.sort.apply(sortFields);
          await ctx.sync();
          return ok({ range, sorted: true, sortKeys });
        }
        if (filterColumn && filterValues) {
          ws.autoFilter.apply(targetRange);
          await ctx.sync();
          ws.autoFilter.apply(targetRange, parseInt(filterColumn) || 0, {
            filterOn: Excel.FilterOn.values,
            values: filterValues,
          });
          await ctx.sync();
          return ok({ range, filtered: true, column: filterColumn, values: filterValues });
        }
        return err("Must provide sortKeys, filterColumn+filterValues, or clearFilters=true.");
      });
    },
  },

  get_filtered_data: {
    schema: {
      type: "function",
      function: {
        name: "get_filtered_data",
        description: "Get only the visible rows from a filtered table.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
            table: { type: "string", description: "Table name." },
          },
          required: ["sheet", "table"],
        },
      },
    },
    mutating: false,
    minApi: "1.9",
    async run({ sheet, table }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const tbl = ws.tables.getItemOrNullObject(table);
        await ctx.sync();
        if (tbl.isNullObject) return err(`Table "${table}" not found.`);
        const visibleRange = tbl.getDataBodyRange().getVisibleView();
        visibleRange.load("values,rowCount,columnCount");
        await ctx.sync();
        return ok({
          table,
          csv: toCsv(visibleRange.values),
          rows: visibleRange.rowCount,
          columns: visibleRange.columnCount,
        });
      });
    },
  },

  // ============================================================================
  // DATA VALIDATION
  // ============================================================================

  apply_data_validation: {
    schema: {
      type: "function",
      function: {
        name: "apply_data_validation",
        description:
          "Apply data validation rules (dropdown lists, number/date constraints, custom formulas). This MODIFIES the workbook. Validation types: list, wholeNumber, decimal, textLength, date, custom.",
        parameters: {
          type: "object",
          properties: {
            sheet: { type: "string", description: "Worksheet name." },
            range: { type: "string", description: "A1 range to validate." },
            validationType: {
              type: "string",
              enum: ["list", "wholeNumber", "decimal", "textLength", "date", "custom"],
              description: "Type of validation.",
            },
            rule: {
              type: "object",
              description: "Validation rule object.",
              properties: {
                source: { type: "string", description: "For list: comma-separated values or range reference." },
                formula1: { type: "string", description: "For number/date: first formula/value." },
                formula2: { type: "string", description: "For number/date: second formula/value (for between)." },
                operator: {
                  type: "string",
                  description: "For number/date: comparison operator.",
                },
                formula: { type: "string", description: "For custom: custom formula." },
              },
            },
            errorMessage: { type: "string", description: "Error alert message." },
            errorTitle: { type: "string", description: "Error alert title." },
            promptMessage: { type: "string", description: "Input prompt message." },
            promptTitle: { type: "string", description: "Input prompt title." },
            clear: { type: "boolean", description: "Clear validation if true." },
          },
          required: ["sheet", "range", "validationType"],
        },
      },
    },
    mutating: true,
    minApi: "1.8",
    async run({ sheet, range, validationType, rule, errorMessage, errorTitle, promptMessage, promptTitle, clear }) {
      return Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
        await ctx.sync();
        if (ws.isNullObject) return err(`Sheet "${sheet}" not found.`);
        const targetRange = ws.getRange(range);
        if (clear) {
          targetRange.dataValidation.clear();
          await ctx.sync();
          return ok({ range, cleared: true });
        }
        if (validationType === "list" && rule.source) {
          targetRange.dataValidation.rule = {
            list: { inCellDropdown: true, source: rule.source },
          };
        } else if (validationType === "wholeNumber" && rule.formula1) {
          targetRange.dataValidation.rule = {
            wholeNumber: { formula1: rule.formula1, formula2: rule.formula2, operator: rule.operator || "between" },
          };
        } else if (validationType === "decimal" && rule.formula1) {
          targetRange.dataValidation.rule = {
            decimal: { formula1: rule.formula1, formula2: rule.formula2, operator: rule.operator || "between" },
          };
        } else if (validationType === "textLength" && rule.formula1) {
          targetRange.dataValidation.rule = {
            textLength: { formula1: rule.formula1, formula2: rule.formula2, operator: rule.operator || "between" },
          };
        } else if (validationType === "date" && rule.formula1) {
          targetRange.dataValidation.rule = {
            date: { formula1: rule.formula1, formula2: rule.formula2, operator: rule.operator || "between" },
          };
        } else if (validationType === "custom" && rule.formula) {
          targetRange.dataValidation.rule = {
            custom: { formula: rule.formula },
          };
        } else {
          return err("Invalid validationType or missing rule parameters.");
        }
        if (errorMessage) {
          targetRange.dataValidation.errorAlert = {
            message: errorMessage,
            showAlert: true,
            style: Excel.DataValidationAlertStyle.stop,
            title: errorTitle || "Invalid Input",
          };
        }
        if (promptMessage) {
          targetRange.dataValidation.prompt = {
            message: promptMessage,
            showPrompt: true,
            title: promptTitle || "Input",
          };
        }
        await ctx.sync();
        return ok({ range, validationType, applied: true });
      });
    },
  },
};

// Array of JSON schemas to send to the model.
export function toolSchemas() {
  return Object.values(tools).map((t) => t.schema);
}

// Dispatch a tool call by name.
export async function runTool(name, args) {
  const tool = tools[name];
  if (!tool) return err(`Unknown tool: ${name}`);
  if (tool.minApi && !isApiSupported(tool.minApi)) {
    return err(
      `Tool "${name}" requires ExcelApi ${tool.minApi}, which this Excel version does not support. ` +
        `Please update to Microsoft 365, a newer Office 2021 build, or Excel on the web.`
    );
  }
  try {
    return await tool.run(args || {});
  } catch (e) {
    return err(e && e.message ? e.message : String(e));
  }
}

export function isMutating(name) {
  return !!(tools[name] && tools[name].mutating);
}
